import React, { useState, useEffect, useRef } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import apiService from '../../services/api';
import { UploadFileType } from '../../types/api';

interface RequestDocumentsModalProps {
    isOpen: boolean;
    onClose: () => void;
    token: string;
    preSelectedIds?: number[];
    preSelectedMessage?: string;
    onSubmit?: (selectedDocuments: string[], notes?: string, selectedIds?: number[]) => void | Promise<void>;
}

export const RequestDocumentsModal: React.FC<RequestDocumentsModalProps> = ({
    isOpen,
    onClose,
    token,
    preSelectedIds = [],
    preSelectedMessage = '',
    onSubmit,
}) => {
    const [documentOptions, setDocumentOptions] = useState<UploadFileType[]>([]);
    const [isLoadingTypes, setIsLoadingTypes] = useState(false);
    const [selectedDocuments, setSelectedDocuments] = useState<string[]>([]);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [notes, setNotes] = useState<string>('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const preSelectionApplied = useRef(false);

    // Reset state and flag when modal closes
    useEffect(() => {
        if (!isOpen) {
            setSelectedDocuments([]);
            setSelectedIds([]);
            setNotes('');
            setDocumentOptions([]);
            preSelectionApplied.current = false;
        }
    }, [isOpen]);

    // Fetch document types when modal opens
    useEffect(() => {
        if (!isOpen) return;

        const fetchDocumentTypes = async () => {
            setIsLoadingTypes(true);
            try {
                const types = await apiService.getUploadFileTypes({ token, lang: 'HEB' });
                const sorted = [...types].sort((a, b) => a.file_upload_order - b.file_upload_order);
                setDocumentOptions(sorted);
            } catch (error) {
                console.error('Failed to load document types:', error);
            } finally {
                setIsLoadingTypes(false);
            }
        };

        fetchDocumentTypes();
    }, [isOpen, token]);

    // Apply pre-selection once document options are loaded
    useEffect(() => {
        if (!isOpen || documentOptions.length === 0 || preSelectionApplied.current) return;

        if (preSelectedIds.length > 0) {
            const preSelectedNums = preSelectedIds.map(Number);
            const matching = documentOptions.filter(t => preSelectedNums.includes(Number(t.id)));
            setSelectedIds(matching.map(t => t.id));
            setSelectedDocuments(matching.map(t => t.description));
        }
        if (preSelectedMessage) {
            setNotes(preSelectedMessage);
        }
        preSelectionApplied.current = true;
    }, [isOpen, documentOptions, preSelectedIds, preSelectedMessage]);

    const toggleDocument = (doc: UploadFileType) => {
        setSelectedDocuments(prev =>
            prev.includes(doc.description)
                ? prev.filter(d => d !== doc.description)
                : [...prev, doc.description]
        );
        setSelectedIds(prev =>
            prev.includes(doc.id)
                ? prev.filter(id => id !== doc.id)
                : [...prev, doc.id]
        );
    };

    const handleSubmit = async () => {
        if (selectedDocuments.length === 0) return;

        setIsSubmitting(true);
        try {
            if (onSubmit) {
                await onSubmit(selectedDocuments, notes, selectedIds);
            }
            onClose();
        } catch (error) {
            console.error('Failed to submit document request:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!isOpen) return null;

    return (
        <ModalWrapper modalId="RequestDocumentsModal"
            isOpen={isOpen}
            onClose={onClose}
            title="Request Documents"
            isLoading={isSubmitting}
            width="max-w-md"
        >
            <div className="space-y-4 mb-6">
                {/* Document List */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        רשימת מסמכים
                    </label>

                    <div className="space-y-0.5 max-h-96 overflow-y-auto border border-gray-300 rounded-lg p-3">
                        {isLoadingTypes ? (
                            <div className="flex justify-center py-4">
                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#3a7380]" />
                            </div>
                        ) : (
                            documentOptions.map((doc) => {
                                const isSelected = selectedDocuments.includes(doc.description);
                                return (
                                    <label
                                        key={doc.id}
                                        className={`flex items-center gap-3 py-1 px-2 rounded-md cursor-pointer hover:bg-[#EBF5F5] transition-colors ${
                                            isSelected ? 'bg-[#EBF5F5]' : ''
                                        }`}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleDocument(doc)}
                                            disabled={isSubmitting}
                                            className="w-4 h-4 text-[#366A75] border-gray-300 rounded focus:ring-[#366A75]"
                                        />
                                        <span className="text-sm text-gray-700">{doc.description}</span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Notes */}
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                        הנחיות\הערות נוספות:
                    </label>
                    <textarea
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        className={`${POPUP_STYLES.input} resize-none`}
                        disabled={isSubmitting}
                    />
                </div>

                {selectedDocuments.length > 0 && (
                    <div className="text-sm text-[#366A75]">
                        {selectedDocuments.length} {selectedDocuments.length === 1 ? 'document' : 'documents'} selected
                    </div>
                )}
            </div>

            <div className="flex justify-center gap-4">
                <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || selectedDocuments.length === 0}
                    className={`${POPUP_STYLES.button} ${
                        selectedDocuments.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                >
                    {isSubmitting ? 'Sending...' : 'Send'}
                </button>
                <button
                    onClick={onClose}
                    disabled={isSubmitting}
                    className={POPUP_STYLES.button}
                >
                    Cancel
                </button>
            </div>
        </ModalWrapper>
    );
};
